import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Search, ArrowLeft, LayoutTemplate, Shield, Type, Briefcase, Building, Blocks, Palette, MousePointer2, User, Link, Layers, Code } from 'lucide-react';

const WizardInput = ({ onComplete, onCancel, onLiveUpdate }) => {
  const [step, setStep] = useState(0);
  const [suggestion, setSuggestion] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  
  const [formData, setFormData] = useState({
    businessName: '',
    referenceUrl: '', 
    projectType: 'Full Website', 
    techStack: 'nextjs', // ✨ NEW: Default to Next.js in Wizard
    businessType: '', 
    industry: '',
    focusAreas: [],
    colorPalette: 'AI Auto-Detect',
    hoverEffects: [] 
  });

  const detectContext = (name) => {
    const categories = [
      { pattern: /(donut|burger|pizza|coffee|cafe|bakery|grill|restaurant|bar|bistro|dinner|eats|taco|sushi|hotel|inn|stay|pub|drink|tea|smoothie|juice|cake|sweet|dessert|chef|kitchen|catering)/i, data: { type: 'Business', industry: 'Hospitality & Food', extras: ['Online Ordering', 'Table Reservation', 'Menu Gallery', 'Ingredients/Allergens'], suggestedPalette: 'Olive Garden Feast' } },
      { pattern: /(soft|tech|futuristic|future|robot|ai|data|\bapp\b|cloud|cyber|\bnet\b|\bsys\b|code|\bai\b|\bbot\b|digital|game|play|stream|twitch|discord|esports|crypto|block|nft|web|\bio\b|saas|platform|hub|lab|logic|pixel|byte|server)/i, data: { type: 'Business', industry: 'Technology & Gaming', extras: ['Live Demo', 'Discord Integration', 'API Docs', 'Pricing Matrix'], suggestedPalette: 'Cyberpunk Neon' } },
      { pattern: /(build|construct|home|house|roof|plumb|electr|garden|fix|repair|contract|arch|realty|estate|property|living|reno|paint|carpenter|handy|develop|civic|structure)/i, data: { type: 'Business', industry: 'Real Estate & Construction', extras: ['Project Gallery', 'Virtual Tours', 'Request Quote', 'Property Search'], suggestedPalette: 'Minimalist Mono' } },
      { pattern: /(gym|fit|health|care|clinic|med|doc|dental|yoga|pilates|crossfit|spa|salon|beauty|hair|barber|makeup|skin|lash|nail|massage|therap|recover|wellness|mind|soul)/i, data: { type: 'Business', industry: 'Health & Beauty', extras: ['Booking System', 'Before/After Gallery', 'Stylist Profiles', 'Treatment Menu'], suggestedPalette: 'Botanical Serenity' } },
      { pattern: /(school|learn|tutor|course|class|academy|uni|edu|teach|coach|mentor|train|master|study|skill|know|lesson|workshop)/i, data: { type: 'Business', industry: 'Education & Coaching', extras: ['Course Catalog', 'Student Portal', 'Free Webinar', 'Success Stories'], suggestedPalette: 'Deep Sea' } },
      { pattern: /(wear|cloth|fashion|boutique|shop|store|market|retail|shoes|sneaker|style|brand|apparel|thread|vogue|outfit|gear|merch|women|men|kids)/i, data: { type: 'Business', industry: 'Fashion & Retail', extras: ['Lookbook', 'Size Guide', 'New Arrivals Feed', 'Instagram Shop'], suggestedPalette: 'Pastel Dreamland' } },
      { pattern: /(law|legal|money|fin|bank|invest|capital|partner|associate|consult|group|advisor|crypto|tax|account|audit|insure|wealth|trust)/i, data: { type: 'Business', industry: 'Legal & Financial', extras: ['Client Portal', 'Case Studies', 'Consultation Booking', 'Team Bios'], suggestedPalette: 'Midnight Sunset' } },
      { pattern: /(design|art|photo|video|film|studio|agency|media|create|ink|print|music|sound|record|entertain|event|party|wedding)/i, data: { type: 'Portfolio', industry: 'Designer & Creative', extras: ['Portfolio Gallery', 'Booking Inquiry', 'Vimeo/YouTube Embeds', 'Service Packages'], suggestedPalette: 'Vibrant Startup' } },
      { pattern: /(auto|car|motor|drive|vehicle|mechanic|garage|dealership|wash|repair|tire|racing|transport|logistics|fleet)/i, data: { type: 'Business', industry: 'Automotive & Transport', extras: ['Vehicle Gallery', 'Service Booking', 'Parts Inventory', 'Pricing Estimator'], suggestedPalette: 'Minimalist Mono' } },
      { pattern: /(travel|tour|guide|trip|vacation|explore|adventure|cruise|flight|ticket|destination|resort|expedition)/i, data: { type: 'Business', industry: 'Travel & Tourism', extras: ['Destination Guides', 'Booking Engine', 'Interactive Map', 'Customer Reviews'], suggestedPalette: 'Ocean Sunset' } },
      { pattern: /(charity|donate|cause|nonprofit|ngo|volunteer|community|foundation|hope|care|mission|church|ministry)/i, data: { type: 'Business', industry: 'Non-Profit & Community', extras: ['Donation Portal', 'Event Calendar', 'Impact Metrics', 'Volunteer Sign-up'], suggestedPalette: 'Earthy Forest Hues' } },
      { pattern: /(furniture|decor|sofa|chair|table|desk|cabinet|interior|wood|living room|bedroom|mattress|rug|lighting|lamp|home goods)/i, data: { type: 'Business', industry: 'Home & Decor', extras: ['AR Room Preview', 'Material Swatches', 'Dimensions Guide', 'White-Glove Delivery'], suggestedPalette: 'Furniture Organic' } }
    ];

    const matchedCategory = categories.find(category => category.pattern.test(name));
    return matchedCategory ? matchedCategory.data : null;
  };

  const projectTypesList = ['Full Website', 'Landing Page', 'Contact Page', 'Pricing Page', 'Login / Auth', 'Dashboard', 'Blog / Article'];

  const dynamicIndustries = {
    'Business': [
      'SaaS & Software', 'Technology & Gaming', 'Consulting & Agency', 
      'Health & Beauty', 'Real Estate & Construction', 'Legal & Financial', 
      'Education & Coaching', 'Fashion & Retail', 'Hospitality & Food', 
      'Travel & Tourism', 'Automotive & Transport', 'Home & Decor', 
      'Electronics', 'Non-Profit & Community', 'NFT & Web3', 'Mobile Apps'
    ],
    'Portfolio': [
      'Designer & Creative', 'Developer & Engineer', 'Photographer & Videographer', 
      'Influencer & Streamer', 'Author & Writer', 'Freelance Consultant', 
      'Actor & Model', 'Musician & Band'
    ]
  };

  const industryDataMap = {
    'Consulting & Agency': { extras: ['Case Studies', 'Client Logos', 'Book Consultation', 'Team Bios'], suggestedPalette: 'Minimalist Mono' },
    'Health & Beauty': { extras: ['Booking System', 'Before/After Gallery', 'Stylist Profiles', 'Treatment Menu'], suggestedPalette: 'Botanical Serenity' },
    'Real Estate & Construction': { extras: ['Project Gallery', 'Virtual Tours', 'Request Quote', 'Property Search'], suggestedPalette: 'Minimalist Mono' },
    'Legal & Financial': { extras: ['Client Portal', 'Case Studies', 'Consultation Booking', 'Team Bios'], suggestedPalette: 'Midnight Sunset' },
    'Education & Coaching': { extras: ['Course Catalog', 'Student Portal', 'Free Webinar', 'Success Stories'], suggestedPalette: 'Deep Sea' },
    'Event Planning': { extras: ['Event Gallery', 'RSVP System', 'Vendor List', 'Pricing Packages'], suggestedPalette: 'Pastel Dreamland' },
    'Travel & Tourism': { extras: ['Destination Guides', 'Booking Engine', 'Interactive Map', 'Customer Reviews'], suggestedPalette: 'Ocean Sunset' },
    'Automotive & Transport': { extras: ['Vehicle Gallery', 'Service Booking', 'Parts Inventory', 'Pricing Estimator'], suggestedPalette: 'Minimalist Mono' },
    'Non-Profit & Community': { extras: ['Donation Portal', 'Event Calendar', 'Impact Metrics', 'Volunteer Sign-up'], suggestedPalette: 'Earthy Forest Hues' },
    'Fashion & Retail': { extras: ['Lookbook', 'Size Guide', 'New Arrivals Feed', 'Instagram Shop'], suggestedPalette: 'Pastel Dreamland' },
    'Hospitality & Food': { extras: ['Online Ordering', 'Table Reservation', 'Menu Gallery', 'Ingredients/Allergens'], suggestedPalette: 'Olive Garden Feast' },
    'Electronics': { extras: ['Tech Specs', 'Compare Models', 'Product Reviews', 'Warranty Info'], suggestedPalette: 'Cyberpunk Neon' },
    'Home & Decor': { extras: ['AR Room Preview', 'Material Swatches', 'Dimensions Guide', 'White-Glove Delivery'], suggestedPalette: 'Furniture Organic' },
    'Automotive & Dealerships': { extras: ['Vehicle Gallery', 'Test Drive Booking', 'Finance Calculator', 'Trade-in Estimator'], suggestedPalette: 'Fiery Ocean' },
    'SaaS & Software': { extras: ['Pricing Table', 'Feature Matrix', 'API Documentation', 'Integration Partners'], suggestedPalette: 'Vibrant Startup' },
    'Technology & Gaming': { extras: ['Live Demo', 'Discord Integration', 'API Docs', 'Pricing Matrix'], suggestedPalette: 'Cyberpunk Neon' },
    'Online Courses': { extras: ['Curriculum Outline', 'Instructor Bios', 'Student Reviews', 'Free Preview'], suggestedPalette: 'Deep Sea' },
    'NFT & Web3': { extras: ['Roadmap Timeline', 'Minting Portal', 'Discord Link', 'Smart Contract Info'], suggestedPalette: 'Cyberpunk Neon' },
    'Mobile Apps': { extras: ['App Store Badges', 'Feature Screenshots', 'Interactive Demo', 'User Reviews'], suggestedPalette: 'Vibrant Startup' },

    'Designer & Creative': { extras: ['Portfolio Gallery', 'Booking Inquiry', 'Vimeo/YouTube Embeds', 'Service Packages'], suggestedPalette: 'Vibrant Startup' },
    'Developer & Engineer': { extras: ['GitHub Projects', 'Tech Stack Grid', 'Resume Download', 'Code Snippets'], suggestedPalette: 'Minimalist Mono' },
    'Photographer & Videographer': { extras: ['High-Res Gallery', 'Booking Calendar', 'Pricing Packages', 'Client Portal'], suggestedPalette: 'Minimalist Mono' },
    'Influencer & Streamer': { extras: ['Social Links Grid', 'Live Stream Embed', 'Merch Store', 'Sponsor Logos'], suggestedPalette: 'Cyberpunk Neon' },
    'Author & Writer': { extras: ['Book Pre-order', 'Chapter Excerpts', 'Author Bio', 'Newsletter Signup'], suggestedPalette: 'Autumn Harvest' },
    'Freelance Consultant': { extras: ['Service Packages', 'Client Testimonials', 'Booking Calendar', 'Case Studies'], suggestedPalette: 'Minimalist Mono' },
    'Actor & Model': { extras: ['Headshot Gallery', 'Showreel Video', 'Measurements/Stats', 'Press & Articles'], suggestedPalette: 'Royal Amethyst' },
    'Musician & Band': { extras: ['Tour Dates', 'Spotify/Apple Music Player', 'Merch Store', 'Music Video Embed'], suggestedPalette: 'Midnight Sunset' }
  };

  const baseFocusAreas = ['Contact Form', 'Testimonials', 'Pricing Tables', 'FAQ Section', 'About Us', 'Newsletter', 'Dark Mode Toggle', 'Live Chat', 'Search Bar', 'Social Media Feed'];

  const visualPalettes = [
    { id: 'Fiery Ocean', name: 'Fiery Ocean', colors: ['#7D0000', '#C21820', '#F2E8D4', '#083248', '#6A9BB8'] },
    { id: 'Olive Garden Feast', name: 'Olive Garden Feast', colors: ['#6B7A41', '#2C3E1F', '#F8F4E6', '#DBA463', '#C57127'] },
    { id: 'Ocean Sunset', name: 'Ocean Sunset', colors: ['#0A1D24', '#007A86', '#A0D2C2', '#F2C687', '#D5551A'] },
    { id: 'Pastel Dreamland', name: 'Pastel Dreamland', colors: ['#D1BFE1', '#F6CDEA', '#FFB7D1', '#C2E0F9', '#9ECBFF'] },
    { id: 'Autumn Harvest', name: 'Autumn Harvest', colors: ['#61201D', '#AE8445', '#492D1B', '#B86F2C', '#FFE1A6'] },
    { id: 'Deep Sea', name: 'Deep Sea', colors: ['#172335', '#26344A', '#4A627E', '#728CA6', '#E2E5E8'] },
    { id: 'Earthy Forest Hues', name: 'Earthy Forest Hues', colors: ['#D6D5C3', '#A8B197', '#648356', '#486846', '#2F4836'] },
    { id: 'Soft Sand', name: 'Soft Sand', colors: ['#F2F1EC', '#DCD5CC', '#EFE7DE', '#E4D6C9', '#D7C3B7'] },
    { id: 'Cyberpunk Neon', name: 'Cyberpunk Neon', colors: ['#0D0221', '#261447', '#FF3864', '#2DE2E6', '#F6FACB'] },
    { id: 'Vibrant Startup', name: 'Vibrant Startup', colors: ['#0A2540', '#4A40E4', '#00D4FF', '#FFD700', '#F6F9FC'] },
    { id: 'Minimalist Mono', name: 'Minimalist Mono', colors: ['#000000', '#1C1C1C', '#454545', '#E5E5E5', '#FFFFFF'] },
    { id: 'Royal Amethyst', name: 'Royal Amethyst', colors: ['#1A0B2E', '#321549', '#6B4C8A', '#D4AF37', '#F3E8FF'] },
    { id: 'Botanical Serenity', name: 'Botanical Serenity', colors: ['#1B3022', '#395144', '#AA8B56', '#E8DFCA', '#F5EFE6'] },
    { id: 'Midnight Sunset', name: 'Midnight Sunset', colors: ['#2E112D', '#540D6E', '#EE4266', '#FFD23F', '#F3FCF0'] },
    { id: 'furniture', name: 'Furniture Organic', colors: ['#0D6533', '#98C45F', '#CFE7B1', '#063A1D', '#98C45F'] }
  ];

  const hoverEffectOptions = [
    { id: 'Product Image Swap', label: '📸 Image Swap', desc: 'Show secondary product image on hover.', preview: (<div className="w-full h-full relative bg-slate-800 flex items-center justify-center text-4xl"><div className="absolute inset-0 flex items-center justify-center transition-opacity duration-500 group-hover/card:opacity-0">👕</div><div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover/card:opacity-100">🧥</div></div>) },
    { id: 'Quick Add to Cart Reveal', label: '🛒 Quick Add Reveal', desc: 'Slide up an Add to Cart button from the bottom.', preview: (<div className="w-full h-full relative bg-slate-800 flex items-center justify-center text-4xl overflow-hidden">👟<div className="absolute bottom-0 left-0 w-full bg-blue-600 text-white text-xs font-bold py-2 text-center translate-y-full group-hover/card:translate-y-0 transition-transform duration-300">+ Add to Cart</div></div>) },
    { id: 'Scale & Soft Glow', label: '✨ Scale & Glow', desc: 'Card slightly enlarges with a soft drop shadow.', preview: (<div className="w-full h-full bg-slate-900 flex items-center justify-center"><div className="w-16 h-20 bg-slate-700 rounded-md transition-all duration-300 group-hover/card:scale-110 group-hover/card:shadow-[0_0_20px_rgba(255,255,255,0.2)] group-hover/card:bg-slate-600"></div></div>) },
    { id: 'Color Swatch Fade-In', label: '🎨 Swatch Reveal', desc: 'Fade in available color dots on the card.', preview: (<div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center relative gap-2"><div className="text-4xl transition-transform duration-300 group-hover/card:-translate-y-2">👜</div><div className="flex gap-2 absolute bottom-3 opacity-0 translate-y-2 group-hover/card:opacity-100 group-hover/card:translate-y-0 transition-all duration-300"><div className="w-3 h-3 rounded-full bg-red-500 shadow-lg border border-white/20"></div><div className="w-3 h-3 rounded-full bg-blue-500 shadow-lg border border-white/20"></div><div className="w-3 h-3 rounded-full bg-green-500 shadow-lg border border-white/20"></div></div></div>) },
    { id: 'Price Tag Flip', label: '🏷️ Price Flip', desc: 'Flip the price into a discount or CTA text.', preview: (<div className="w-full h-full bg-slate-800 flex items-center justify-center relative overflow-hidden"><div className="text-lg font-bold text-slate-300 transition-transform duration-300 group-hover/card:-translate-y-10 absolute">$99.00</div><div className="text-lg font-bold text-blue-400 transition-transform duration-300 translate-y-10 group-hover/card:translate-y-0 absolute">Buy Now →</div></div>) },
    { id: 'Quick View Overlay', label: '👁️ Quick View', desc: 'Reveal a darkened overlay with a Quick View icon.', preview: (<div className="w-full h-full bg-slate-800 flex items-center justify-center relative"><div className="text-4xl">⌚</div><div className="absolute inset-0 bg-black/60 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]"><div className="bg-white/20 p-2 rounded-full border border-white/30 text-white transform scale-50 group-hover/card:scale-100 transition-transform duration-300"><Search size={16} /></div></div></div>) }
  ];

  const updateData = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleNext = () => setStep(prev => prev + 1);

  const toggleHoverEffect = (effect) => {
    setFormData(prev => {
      const current = prev.hoverEffects;
      if (current.includes(effect)) return { ...prev, hoverEffects: current.filter(e => e !== effect) };
      return { ...prev, hoverEffects: [...current, effect] };
    });
  };

  useEffect(() => {
    if (step === 0) {
        if (formData.businessName.length > 2) {
            setIsScanning(true);
            const timer = setTimeout(() => {
                const guess = detectContext(formData.businessName);
                setSuggestion(guess);
                setIsScanning(false);
            }, 600);
            return () => clearTimeout(timer);
        } else {
            setSuggestion(null);
            setIsScanning(false);
        }
    }
  }, [formData.businessName, step]);

  useEffect(() => {
    if (onLiveUpdate) {
      onLiveUpdate({ formData, step, suggestion });
    }
  }, [formData, step, suggestion, onLiveUpdate]);

  const applySuggestion = () => {
    if (suggestion) {
        setFormData(prev => ({ ...prev, businessType: suggestion.type, industry: suggestion.industry, colorPalette: 'AI Auto-Detect' }));
        setStep(1); 
    }
  };

  const toggleFocus = (area) => {
    setFormData(prev => {
      const current = prev.focusAreas;
      if (current.includes(area)) return { ...prev, focusAreas: current.filter(a => a !== area) };
      return { ...prev, focusAreas: [...current, area] };
    });
  };

  const handleSelectAll = () => {
    const allOptions = [...baseFocusAreas, ...(suggestion?.extras || [])];
    if (formData.focusAreas.length >= allOptions.length) updateData('focusAreas', []);
    else updateData('focusAreas', allOptions);
  };

  // 🚀 WIZARD PROMPT GENERATOR (Dynamically handles Next.js or Vanilla HTML)
  const generateFinalPrompt = () => {
    const selectedVisualPalette = visualPalettes.find(p => p.id === formData.colorPalette);
    let finalPaletteString = '';
    
    if (formData.colorPalette === 'AI Auto-Detect' && suggestion?.suggestedPalette) {
        const matchedAIPalette = visualPalettes.find(p => p.name === suggestion.suggestedPalette);
        finalPaletteString = matchedAIPalette 
            ? `${matchedAIPalette.name}. Design Requirement: Use these exact hex codes: ${matchedAIPalette.colors.join(', ')}.` 
            : suggestion.suggestedPalette;
    } else if (selectedVisualPalette) {
        finalPaletteString = `${selectedVisualPalette.name}. Design Requirement: Use these exact hex codes: ${selectedVisualPalette.colors.join(', ')}.`;
    } else {
        finalPaletteString = formData.colorPalette;
    }

    const hoverInstruction = formData.hoverEffects.length > 0 
      ? `\n- **Hover States:** Implement these effects: ${formData.hoverEffects.join(', ')}.` 
      : '';

    const safeProjectType = formData.projectType === 'Login / Auth' ? 'User Onboarding UI Mockup' : formData.projectType;
    const isSubPage = formData.projectType !== 'Full Website';
    const isNext = formData.techStack === 'nextjs';

    const focusAreasText = formData.focusAreas.length > 0 
      ? formData.focusAreas.join(', ') 
      : 'Standard essential layout elements';

    // ✨ Dynamic Instructions based on Tech Stack
    const layoutInstructions = isSubPage 
      ? `- **EXTRACTION SCOPE (CRITICAL):** This is a sub-page or sub-component. Do NOT build a full website. DO NOT generate a Header, Footer, or Root Layout. Output ONLY the code for this specific view.`
      : `- **Component Structure:** Build a fully responsive page broken down into clean, separate, and modular components.
- **Mandatory Advanced Components:**
  - **HEADER:** Include: Logo, Search Bar, Main Nav, Mega Menu, Cart, and CTAs.
  - **FOOTER:** Include: Navigation links explicitly split into TWO (2) columns, and a dedicated, visually distinct Newsletter Subscription section.`;

    const techInstruction = isNext 
      ? `- Next.js (App Router)\n- TypeScript (.tsx ONLY)\n- Tailwind CSS ONLY\n- Framer Motion\n- Lucide React (Absolutely NO emojis)\n- Shadcn UI (Only if building Admin/Dashboard)` 
      : `- Semantic HTML5\n- Vanilla CSS3 (Custom raw CSS, NO Tailwind)\n- Vanilla JavaScript\n- Inline SVG Icons (Absolutely NO emojis)`;

    const outputInstruction = isNext
      ? `1. Project Structure (/app, /components)\n2. Dependencies\n3. Full Code (.tsx ONLY, Tailwind ONLY, File-separated format e.g., \`// === FILE: /components/Header.tsx ===\`)`
      : `1. Single index.html architecture (or clear HTML/CSS/JS separation)\n2. Full Code (Raw HTML/CSS/JS)`;

    const prompt = `## 1. Role / Persona
- Top 0.1% Full-Stack Engineer
- Awwwards-level Creative Developer
- Product Designer

## 2. Core Specializations
- Crafting visually striking, emotionally engaging interfaces
- Building conversion-focused, premium UI systems

## 3. Main Objective
- Design and build a FULLY ORIGINAL, production-grade ${safeProjectType} for a conceptual ${formData.industry} brand named '${formData.businessName}'.

## 4. Critical Creative Constraints
- Output must feel: Premium, Modern, Intentional, Awwwards-worthy

## 5. Design Thinking Layer (MANDATORY)
### 5.1 Industry Detection
- Industry: ${formData.industry}
- Category: ${formData.businessType}

### 5.2 Component Adaptation
- Build sections tailored to this industry. Key Sections: ${focusAreasText}${hoverInstruction}

### 5.3 Visual Concept
- Define a UNIQUE design direction. Avoid generic layouts.

## 6. Design & Aesthetic Requirements
### 6.1 Visual Identity
- ${finalPaletteString}

## 7. Technology Stack Requirements (STRICT)
${techInstruction}

## 8. Architecture & Scope
${layoutInstructions}
- Abstract repeated UI into reusable components.

## 9. Output Format Requirements
${outputInstruction}
- Design System Summary

## 10. Quality Expectations
- Clean architecture, premium design. NO CHAT. START CODING IMMEDIATELY.`;
    
    onComplete({ prompt: prompt, referenceUrl: formData.referenceUrl });
  };
  
  const slideIn = { hidden: { opacity: 0, x: 20 }, visible: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -20 } };

  const wizardSteps = [
    { title: "Business Identity", icon: <Type size={18}/> },
    { title: "Project Scope", icon: <LayoutTemplate size={18}/> },
    { title: "Project Category", icon: <Briefcase size={18}/> },
    { title: "Industry Sector", icon: <Building size={18}/> },
    { title: "Module Selection", icon: <Blocks size={18}/> },
    { title: "Color Architecture", icon: <Palette size={18}/> },
    { title: "Interactivity", icon: <MousePointer2 size={18}/> },
  ];

  return (
    <div className="bg-[#0B0E14] border border-gray-800 rounded-2xl w-full max-w-4xl mx-auto shadow-2xl relative flex flex-col h-[750px] sm:h-[700px] overflow-hidden">
      
      <div className="w-full bg-[#0B0E14] border-b border-gray-800/50 p-5 sm:p-8 flex flex-col z-20 shadow-lg">
        <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg border border-blue-500/30 flex items-center justify-center bg-blue-900/10 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                    <Shield className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-lg font-bold text-white tracking-wide">Sentinel<span className="font-light text-gray-400">PROMPT</span></h1>
            </div>
            <button onClick={onCancel} className="text-gray-500 hover:text-white transition p-2 rounded-full hover:bg-gray-800">✕</button>
        </div>

        <div className="relative flex items-center justify-between w-full px-2 sm:px-4 mb-2 sm:mb-6">
            <div className="absolute left-[5%] right-[5%] top-1/2 -translate-y-1/2 h-1 bg-gray-800 rounded-full z-0"></div>
            <div className="absolute left-[5%] top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full z-0 transition-all duration-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" style={{ width: `${(step / (wizardSteps.length - 1)) * 90}%` }}></div>

            {wizardSteps.map((s, index) => {
                const isActive = index === step;
                const isPast = index < step;
                
                return (
                    <div key={index} className="relative z-10 flex flex-col items-center">
                        <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                            isActive ? 'border-2 border-blue-400 bg-[#0B0E14] text-blue-400 shadow-[0_0_15px_rgba(96,165,250,0.6)] scale-110' : isPast ? 'border-2 border-blue-600 bg-blue-600 text-white' : 'border-2 border-gray-800 bg-[#0B0E14] text-gray-600'
                        }`}>
                            <div className="scale-75 sm:scale-100">{s.icon}</div>
                        </div>
                    </div>
                );
            })}
        </div>
        
        <div className="text-center text-sm font-bold text-blue-400 uppercase tracking-widest mt-6">
            Phase {step + 1} — {wizardSteps[step].title}
        </div>
      </div>

      <div className="flex-1 relative flex flex-col bg-[#0A0C10] p-6 sm:p-10 overflow-y-auto custom-scrollbar">
        <div className="flex items-center mb-8">
            {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="p-2 mr-4 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700 transition-all border border-transparent hover:border-gray-600" title="Go Back">
                <ArrowLeft className="w-5 h-5" /> 
            </button>
            )}
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                {["Project Name?", "What are we building?", "Project Category:", "Select Industry:", "Select Features:", "Color Palette:", "Hover Effects:"][step]}
            </h2>
        </div>

        <div className="flex-1 relative flex flex-col">
            <AnimatePresence mode='wait'>
            
            {/* STEP 0: NAME & URL OVERRIDE */}
            {step === 0 && (
                <motion.div key="step0" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col">
                <div className="relative">
                    <input autoFocus type="text" className="w-full bg-gray-900/50 text-white text-xl p-5 rounded-xl border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all shadow-inner" placeholder="e.g. Urban Builder, TechFlow..." value={formData.businessName} onChange={(e) => updateData('businessName', e.target.value)} onKeyDown={(e) => e.key === 'Enter' && formData.businessName && (suggestion ? applySuggestion() : handleNext())} />
                    {isScanning && <div className="absolute right-5 top-5 text-blue-400 animate-pulse flex items-center gap-2 font-medium"><Search size={18}/> Scanning...</div>}
                </div>

                <div className="relative mt-4">
                    <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                        <Link size={14} className="text-purple-400"/> Design Reference URL (Optional)
                    </label>
                    <input 
                        type="url" 
                        className="w-full bg-gray-900/50 text-white text-sm p-4 rounded-xl border border-gray-700 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all shadow-inner" 
                        placeholder="e.g. https://stripe.com (We will extract their design & colors)" 
                        value={formData.referenceUrl || ''} 
                        onChange={(e) => updateData('referenceUrl', e.target.value)} 
                        onKeyDown={(e) => e.key === 'Enter' && formData.businessName && (suggestion ? applySuggestion() : handleNext())} 
                    />
                </div>

                <AnimatePresence>
                    {suggestion && !isScanning && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={applySuggestion} className="mt-6 p-4 sm:p-5 bg-blue-900/10 border border-blue-500/30 rounded-xl cursor-pointer hover:border-blue-500/60 hover:bg-blue-900/20 transition-all flex items-center gap-3 sm:gap-4 group w-full overflow-hidden">
                            <div className="bg-blue-600/20 p-2.5 sm:p-3 rounded-full text-blue-400 border border-blue-500/30 shrink-0"><Sparkles className="w-4 h-4 sm:w-5 sm:h-5" /></div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs sm:text-sm text-blue-300 font-bold tracking-wide truncate">AI DETECTED: {suggestion.industry.toUpperCase()}</div>
                                <div className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate">Click to auto-apply settings for <b className="text-gray-300">{suggestion.type}</b></div>
                            </div>
                            <div className="hidden sm:block ml-auto text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity font-bold text-sm shrink-0">Apply →</div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="mt-auto pt-8 flex justify-end">
                    <button disabled={!formData.businessName} onClick={handleNext} className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600 text-white font-medium px-8 py-3.5 rounded-lg transition-all w-full sm:w-auto shadow-[0_0_15px_rgba(37,99,235,0.2)]">Next →</button>
                </div>
                </motion.div>
            )}

            {/* STEP 1: PROJECT SCOPE & TECH STACK */}
            {step === 1 && (
                <motion.div key="step1" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col pb-8">
                
                {/* ✨ NEW: Tech Stack Selector in Wizard */}
                <div className="mb-6 bg-gray-900/50 p-1.5 rounded-xl w-full border border-gray-800 shadow-md flex">
                    <button onClick={() => updateData('techStack', 'nextjs')} className={`flex-1 py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${formData.techStack === 'nextjs' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}>
                        <Layers size={16}/> Next.js/React
                    </button>
                    <button onClick={() => updateData('techStack', 'html')} className={`flex-1 py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${formData.techStack === 'html' ? 'bg-green-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}>
                        <Code size={16}/> HTML/CSS/JS
                    </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {projectTypesList.map(type => (
                    <button key={type} onClick={() => { updateData('projectType', type); handleNext(); }} className="p-5 bg-gray-800/30 border border-gray-700/50 rounded-xl text-left hover:border-blue-500 hover:bg-gray-800/80 transition-all flex flex-col items-start gap-3 group">
                        <LayoutTemplate className="w-6 h-6 text-gray-500 group-hover:text-blue-400 transition-colors" />
                        <span className="text-sm font-medium text-white">{type}</span>
                    </button>
                    ))}
                </div>
                </motion.div>
            )}

            {/* STEP 2: BINARY CATEGORY */}
            {step === 2 && (
                <motion.div key="step2" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col pb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {[
                        { id: 'Business', label: 'Commercial Business', desc: 'Agencies, SaaS, Retail, Services', icon: <Briefcase size={40} /> },
                        { id: 'Portfolio', label: 'Personal Portfolio', desc: 'Designers, Creators, Freelancers', icon: <User size={40} /> }
                    ].map(type => (
                    <button 
                        key={type.id} 
                        onClick={() => { updateData('businessType', type.id); handleNext(); }} 
                        className="p-8 bg-gray-800/30 border border-gray-700/50 rounded-2xl text-center hover:border-blue-500 hover:bg-gray-800/80 transition-all group flex flex-col items-center justify-center gap-5 hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]"
                    >
                        <div className="text-gray-500 group-hover:text-blue-400 transition-colors bg-gray-900/50 p-4 rounded-full group-hover:bg-blue-900/20">{type.icon}</div>
                        <div>
                            <div className="text-xl font-bold text-white mb-2">{type.label}</div>
                            <div className="text-sm text-gray-400 leading-relaxed">{type.desc}</div>
                        </div>
                    </button>
                    ))}
                </div>
                </motion.div>
            )}

            {/* STEP 3: INDUSTRY */}
            {step === 3 && (
                <motion.div key="step3" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col pb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto max-h-[350px] custom-scrollbar pr-2 pb-2">
                    {(dynamicIndustries[formData.businessType] || dynamicIndustries['Business']).map(ind => (
                    <button 
                        key={ind} 
                        onClick={() => { 
                            updateData('industry', ind); 
                            setSuggestion({
                                industry: ind,
                                type: formData.businessType,
                                extras: industryDataMap[ind]?.extras || [],
                                suggestedPalette: industryDataMap[ind]?.suggestedPalette || 'Minimalist Mono'
                            });
                            handleNext(); 
                        }} 
                        className="w-full text-left p-4 rounded-xl bg-gray-800/30 border border-gray-700/50 hover:border-blue-500 hover:bg-gray-800/80 text-gray-200 text-sm transition-all hover:shadow-[0_0_15px_rgba(59,130,246,0.1)]"
                    >
                        {ind}
                    </button>
                    ))}
                </div>
                </motion.div>
            )}

            {/* STEP 4: FEATURES */}
            {step === 4 && (
                <motion.div key="step4" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col pb-8">
                <div className="flex items-center justify-between mb-5">
                    <span className="text-gray-400 text-sm">Select the components to generate:</span>
                    <button onClick={handleSelectAll} className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">Select All</button>
                </div>
                
                {suggestion && suggestion.extras && (
                    <div className="mb-5">
                        <p className="text-xs text-blue-400/80 uppercase font-bold mb-3 tracking-wider">Recommended for {formData.industry}</p>
                        <div className="flex flex-wrap gap-2">
                            {suggestion.extras.map(area => (
                                <button key={area} onClick={() => toggleFocus(area)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${formData.focusAreas.includes(area) ? 'bg-blue-600/20 text-blue-400 border-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.1)]' : 'bg-gray-800/30 text-gray-400 border-gray-700/50 hover:border-gray-500 hover:text-gray-200'}`}>{formData.focusAreas.includes(area) ? '✓ ' : '+ '}{area}</button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap gap-2 mb-4 overflow-y-auto max-h-[250px] custom-scrollbar pr-2">
                    {baseFocusAreas.map(area => (
                    <button key={area} onClick={() => toggleFocus(area)} className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${formData.focusAreas.includes(area) ? 'bg-blue-600/20 text-blue-400 border-blue-500/50 shadow-[0_0_10px_rgba(59,130,246,0.1)]' : 'bg-transparent text-gray-400 border-gray-700/50 hover:border-gray-500 hover:text-gray-200'}`}>{formData.focusAreas.includes(area) ? '✓ ' : '+ '}{area}</button>
                    ))}
                </div>
                
                <div className="mt-auto pt-4 flex justify-end">
                    <button onClick={handleNext} className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-8 py-3.5 rounded-lg transition-all w-full sm:w-auto shadow-[0_0_15px_rgba(37,99,235,0.2)]">Next →</button>
                </div>
                </motion.div>
            )}

            {/* STEP 5: COLOR PALETTE */}
            {step === 5 && (
                <motion.div key="step5" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col pb-8">
                <button onClick={() => updateData('colorPalette', 'AI Auto-Detect')} className={`w-full p-5 mb-5 rounded-xl text-left border transition-all ${formData.colorPalette === 'AI Auto-Detect' ? 'bg-blue-900/10 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.15)]' : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-500'}`}>
                    <div className="text-lg font-bold text-white flex items-center gap-2"><Sparkles className="text-blue-400" size={18} /> AI Auto-Detect</div>
                    <div className="text-sm text-gray-400 mt-1">{suggestion?.suggestedPalette ? `Recommended: ${suggestion.suggestedPalette}` : 'Let the Creative Director decide'}</div>
                </button>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 overflow-y-auto max-h-[300px] custom-scrollbar pr-2 pb-2">
                    {visualPalettes.map(palette => (
                    <button key={palette.id} onClick={() => updateData('colorPalette', palette.id)} className={`p-4 rounded-xl border transition-all flex flex-col gap-3 ${formData.colorPalette === palette.id ? 'bg-blue-900/10 border-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.1)]' : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-500'}`}>
                        <div className="flex h-10 w-full rounded-lg overflow-hidden shadow-sm">{palette.colors.map((hex, idx) => (<div key={idx} className="flex-1" style={{ backgroundColor: hex }}></div>))}</div>
                        <div className="text-sm font-bold text-gray-200 text-left w-full pl-1">{palette.name}</div>
                    </button>
                    ))}
                </div>
                
                <div className="mt-auto pt-4 flex justify-end">
                    <button onClick={handleNext} className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-8 py-3.5 rounded-lg transition-all w-full sm:w-auto shadow-[0_0_15px_rgba(37,99,235,0.2)]">Next →</button>
                </div>
                </motion.div>
            )}

            {/* STEP 6: HOVER EFFECTS */}
            {step === 6 && (
                <motion.div key="step6" variants={slideIn} initial="hidden" animate="visible" exit="exit" className="flex-1 flex flex-col pb-8">
                <span className="text-gray-400 text-sm block mb-5">Hover over the cards to preview interactivity:</span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 overflow-y-auto max-h-[350px] custom-scrollbar pr-2 pb-2">
                    {hoverEffectOptions.map(effect => {
                    const isSelected = formData.hoverEffects.includes(effect.id);
                    return (
                        <button key={effect.id} onClick={() => toggleHoverEffect(effect.id)} className={`relative rounded-xl text-left border transition-all flex flex-col overflow-hidden group/card ${isSelected ? 'bg-blue-900/10 border-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.15)]' : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-500'}`}>
                        <div className="w-full h-24 relative overflow-hidden border-b border-gray-700/50">
                            {effect.preview}
                            {isSelected && (<div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1 shadow-lg z-10"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path></svg></div>)}
                        </div>
                        <div className="p-4 z-10">
                            <div className="text-sm font-bold text-gray-100 mb-1 group-hover/card:text-blue-400 transition-colors">{effect.label}</div>
                            <div className="text-xs text-gray-500 leading-snug">{effect.desc}</div>
                        </div>
                        </button>
                    );
                    })}
                </div>

                <div className="mt-auto pt-4">
                    <button onClick={generateFinalPrompt} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg py-4 rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.3)] transition transform hover:scale-[1.01] flex items-center justify-center gap-2">
                    <Sparkles size={20} className="text-blue-200" /> Compile Engine
                    </button>
                </div>
                </motion.div>
            )}
            </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default WizardInput;